<template>
  <sModal
    v-model="periodDialog"
    :data-qa="'modal-date-period-' + (periodDialog ? 'open' : 'close')"
    persistent
    :title="$t('Purchasing.Period')"
    width="fit-content">
    <template v-slot:activator>
      <div
        class="data-range-dialog_chip"
        :class="{'is-disabled': disabled}"
        data-qa="btn-modal-date-period-open"
        @click="open()">
        <span>
          {{ currentLabel }}
        </span>
        <sIcon
          class="my-auto"
          color="var(--black-color)"
          size="14"
          type="calendar"/>
      </div>
    </template>
    <div
      class="range-card"
      :class="{'with-periods': showPeriods}">
      <div
        v-if="showPeriods"
        class="grid text-left gap-15 flex-align-top">
        <sButton
          v-for="(periodItem, periodIndex) in currentPeriods"
          :key="periodIndex"
          :data-qa="`btn-period-${periodItem.type}`"
          small
          type="text"
          @click="changePeriod(periodItem)">
          <div
            slot="content"
            class="text-left px-1">
            {{ periodItem.name }}
          </div>
        </sButton>
      </div>
      <div>
        <div 
          v-if="useDate"
          class="mb-4 flex gap-10">
          <div>
            <sDatePicker
              v-model="dateFrom"
              :format="labelFormat"
              :placeholder="$t('Common.DateFrom')"
              :show-second="false"
              :type="componentType"
              :value-type="valueType"/>
          </div>
          <div>
            <sDatePicker
              v-model="dateTo"
              :format="labelFormat"
              :placeholder="$t('Common.DateTo')"
              :type="componentType"
              :value-type="valueType"/>
          </div>
        </div>
        <sDatePicker
          v-model="periodMutable"
          inline
          partial-update
          range
          :show-second="false"
          style="width: 100%;"
          :type="componentType"
          :value-type="valueType"
          @select="periodMutable = $event"/>
      </div>
    </div>
    <template #footer>
      <sButton
        key="btn-apply"
        data-qa="btn-modal-date-period-apply"
        @click="apply()">
        {{ $t('Common.Apply') }}
      </sButton>
    </template>
  </sModal>
</template>
<script>
/**
 * @category dialog
  * Компонент диалога для выбора периода с типом
  
 * @vue-data {Boolean} periodDialog - Отображение диалога выбора периода
 * @vue-computed {String} currentLabel - Текст кнопки выбора периода
*/

import { /*isValidRangeDate, isValidDate, isValidDates,*/ format } from 'toolkit/utils/date'
import { getLocale } from 'toolkit/components/dateComponents/locale'
import { parse } from 'toolkit/utils/date-format-parse/parse.js'

export default {
  name: 'dateRangeDialogWithType',
  data() {
    return {
      periodDialog: false,
      currentLabel: null,
      periods: [
        {
          name: this.$t('Common.DateRange.CurrentDay'),
          type: 'currentDay',
        },
        {
          name: this.$t('Common.DateRange.InWeek'),
          type: 'currentWeek',
        },
        {
          name: this.$t('Common.DateRange.PerMonth'),
          type: 'currentMonth',
        },
        {
          name: this.$t('Common.DateRange.InYear') ,
          type: 'currentYear',
        },
        {
          name: this.$t('Common.DateRange.LastHour') ,
          type: 'lastHour',
        },
        {
          name: this.$t('Common.DateRange.Last12Hours') ,
          type: 'last12Hours',
        },
        {
          name: this.$t('Common.DateRange.Last24Hours') ,
          type: 'last24Hours',
        },
        {
          name: this.$t('Common.DateRange.Last7Days') ,
          type: 'last7Days',
        },
      ],
      periodMutable: null,
    }
  },
  methods: {
    parseDate(value, fmt) {
      fmt = fmt || this.labelFormat
      const backupDate = new Date()
      

      return parse(value, fmt, { locale: getLocale(this.$i18n.locale), backupDate })
    },
    apply() {
      this.$emit('update:search', this.periodMutable)
      this.periodDialog = false
      this.$emit('apply', this.periodMutable)
      this.checkRange(this.periodMutable)
    },
    checkRange(period) {
      let result = this.$t('Purchasing.AllTheTime')
      if (period?.filter(i => i)?.length) {
        const v0 = this.value2date(period[0])
        const v1 = this.value2date(period[1])
        if (!v0 || !v1 || isNaN(v0.getTime()) || isNaN(v1.getTime())) {
          result = format(period[0] ?? period[1], this.labelFormat, getLocale(this.$i18n.locale))
        } else if (
          v0.getTime() === v1.getTime()
        ) {
          result = format(v0, this.labelFormat, getLocale(this.$i18n.locale))
        } else {
          result = [v0, v1].map(v => format(v, this.labelFormat, getLocale(this.$i18n.locale))).join(' - ')
        }
      }
      this.currentLabel = result
    },
    changePeriod(period) {
      if (period?.type) {
        const now = new Date()
        let from, to
        switch (period.type) {
        case 'currentDay':
          from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
          to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
          break
        case 'currentWeek': {
          const day = now.getDay() || 7
          const start = new Date(now)
          start.setDate(now.getDate() - day + 1)
          start.setHours(0, 0, 0, 0)
          const end = new Date(start)
          end.setDate(start.getDate() + 6)
          end.setHours(23, 59, 59, 999)
          from = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0)
          to = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59)
          break
        }
        case 'currentMonth': {
          from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
          const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
          to = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate(), 23, 59, 59)
          break
        }
        case 'currentYear': {
          from = new Date(now.getFullYear(), 0, 1, 0, 0, 0)
          const lastDay = new Date(now.getFullYear(), 11, 31)
          to = new Date(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate(), 23, 59, 59)
          break
        }
        case 'lastHour': {
          from = new Date(now.getTime() - 60 * 60 * 1000)
          to = now
          break
        }
        case 'last12Hours': {
          from = new Date(now.getTime() - 12 * 60 * 60 * 1000)
          to = now
          break
        }
        case 'last24Hours': {
          from = new Date(now.getTime() - 24 * 60 * 60 * 1000)
          to = now
          break
        }
        case 'last7Days': {
          to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
          from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000)
          from.setHours(0, 0, 0, 0)
          break
        }
        default:
          break
        }
        this.dateFrom = from  
        this.dateTo = to
      }
    },
    /**
     * Заполняем данные при открытии диалога выбора периода
     */
    open() {
      if (this.disabled) {
        return
      }
      this.$set(this, 'periodMutable', this.search.slice(0) ?? [])
      this.$nextTick(() => {
        this.periodDialog = true
      })
    },
    value2date(value) {
      switch (this.valueType) {
      case 'date':
        return value instanceof Date ? new Date(value.getTime()) : new Date(NaN)
      default:
        return typeof value === 'string' ? this.parseDate(value, this.valueType) : new Date(NaN)
      }
    },
  },
  computed: {
    labelFormat() {
      switch (this.componentType) {
      case 'datetime':
        return 'DD.MM.YYYY HH:mm'
      case 'time':
        return 'HH:mm'
      case 'year':
        return 'YYYY'
      case 'month':
        return 'MM.YYYY'
      case 'week':
        return 'w'
      default:
        return 'DD.MM.YYYY'
      }
    },
    useTime() {
      return ['datetime', 'time'].includes(this.componentType)
    },
    useDate() {
      return ['date', 'datetime', 'week', 'month', 'year'].includes(this.componentType)
    },
    currentPeriods() {
      if (!this.showPeriods) {
        return []
      }
      let result = []
      if (this.useDate) {
        result = this.periods.filter(p => !['lastHour', 'last12Hours', 'last24Hours'].includes(p.type))
      }
      if (this.useTime) {
        result = result.concat(this.periods.filter(p => ['lastHour', 'last12Hours', 'last24Hours'].includes(p.type)))
      }
      return result
    },
    dateFrom: {
      get() {
        return this.periodMutable?.[0] ?? null
      },
      set(value) {
        if (!this.periodMutable) {
          this.$set(this, 'periodMutable', [null, null])
        }
        if (value) {
          const result = this.valueType === 'date' ? value
            : typeof this.valueType === 'string' ? format(value, this.valueType, getLocale(this.$i18n.locale))
              : value 
          this.$set(this.periodMutable, 0, result)
        } else {
          this.$set(this.periodMutable, 0, null)
        }
      }
    },
    dateTo: {
      get() {
        return this.periodMutable?.[1] ?? null
      },
      set(value) {
        if (!this.periodMutable) {
          this.$set(this, 'periodMutable', [null, null])
        }
        if (value) {
          const result = this.valueType === 'date' ? value
            : typeof this.valueType === 'string' ? format(value, this.valueType, getLocale(this.$i18n.locale))
              : value 
          this.$set(this.periodMutable, 1, result)
        } else {
          this.$set(this.periodMutable, 1, null)
        }
      },
    },
  },
  watch: {
    search: {
      immediate: true,
      deep: true,
      handler(newValue, oldValue) {
        const v0New = newValue?.[0] ? this.value2date(newValue[0]) : null
        const v1New = newValue?.[1] ? this.value2date(newValue[1]) : null
        const v0Old = oldValue?.[0] ? this.value2date(oldValue[0]) : null
        const v1Old = oldValue?.[1] ? this.value2date(oldValue[1]) : null
        const changed =
          !oldValue ||
          v0New?.getTime() !== v0Old?.getTime() ||
          v1New?.getTime() !== v1Old?.getTime()
        if (changed) {
          this.checkRange(newValue)
        }
      }
    }
  },
  props: {
    search: {
      type: Array,
    },
    disabled: {
      type: [Boolean, String],
    },
    showPeriods: {
      type: Boolean,
      default: true,
    },
    /**
    * Тип выбора даты для компонента DatePicker и sCalendarRange. Возможные значения: 'date', 'datetime', 'time', 'year', 'month', 'week'.
    */
    componentType: {
      type: String, // ['date', 'datetime', 'time', 'year', 'month', 'week']
      default: 'date',
      validator: function (value) {
        const correct = ['date', 'datetime', 'time', 'year', 'month', 'week'].indexOf(value) !== -1
        return correct
      },
    },
    /**
    * Формат возвращаемого значения при выборе даты для v-model. Возможные значения: 'date' или любой другой формат, поддерживаемый функцией format из toolkit/utils/date.
    */
    valueType: {
      type: String,
      default: 'date', // date, format like 'YYYY-MM-DD',
    },
  },
}
</script>
<style scoped lang="scss">
.data-range-dialog {
  &_chip {
    min-width: 210px;
    height: 40px;
    border-radius: 31px;
    background-color: var(--white-color) !important;
    border: 2px solid var(--v-main__green-base) !important;
    padding: 5px 12px;
    align-items: center;
    justify-content: space-between;
    display: flex;
    cursor: pointer;
    width: 100%;
    position: relative;
    font-size: 14px;
    transition: all 0.25s ease;
    user-select: none;
    gap: 5px;
    &:before {
      background-color: currentColor;
      bottom: 0;
      border-radius: inherit;
      content: "";
      left: 0;
      opacity: 0;
      position: absolute;
      pointer-events: none;
      right: 0;
      top: 0;
    }
    &:hover:before {
      opacity: .04;
    }
    &:focus:before {
      opacity: .12;
    }
    &.is-disabled {
      border-color: var(--main__green_locked) !important;
      background: var(--secondary__green_locked);
      cursor: not-allowed;
      &:hover:before {
        opacity: 0;
      }
    }
  }
}
.range-card {
  display: grid;
  &.with-periods {
    grid-template-columns: auto 1fr;
    gap: 5px;
  }
}
</style>
